// @flow
import React from 'react';
import type {Node} from 'react';
import {useParams} from 'react-router-dom';
import moment from 'moment';
import {ErrorMessage} from '@wellstone-solutions/web';
import {ALERT_TYPES, showAlert} from 'utils/showAlert';
import {useStores} from 'hooks/useStores';
import {buildIdLookup} from 'utils/buildIdLookup';
import {SaveCancelModal} from 'components/modal/saveCancelModal';
import {DEFAULT_DATE_FORMAT} from 'constants/dates';
import {ItemSelection} from './ItemSelection';
import {useStartEndDateAndId} from '../../hooks/useStartEndDateAndId';
import type {ApiProgramAdmissionType} from '@wellstone-solutions/common/dist/models/types';

type PropTypes = {
  open: boolean,
  setShowModal: (show: boolean) => void,
  programAdmission: ApiProgramAdmissionType,
};

export const AddProgramModal = ({
  open,
  setShowModal,
  programAdmission,
}: PropTypes): Node => {
  const {meStore, memberStore} = useStores();
  const {member: memberId} = useParams();

  const member = memberStore.findMember(memberId);
  const availablePrograms = meStore.myPrograms;
  const memberProgramAdmissions = member.admissions.map((adm) => adm.program);
  const memberProgramLookup = buildIdLookup(memberProgramAdmissions);

  // remove programs that the member is already admitted to
  const selectItems = availablePrograms.reduce((accum, prog) => {
    if (!memberProgramLookup[prog.id]) {
      accum.push({
        label: prog.name,
        value: prog.id,
      });
    }
    return accum;
  }, []);

  const form = useStartEndDateAndId();

  const onSave = async () => {
    if (!form.canSave()) {
      return;
    }

    const admissionInput = {
      memberId: memberId,
      startDate: moment(form.data.startDate).format(DEFAULT_DATE_FORMAT),
      endDate: moment(form.data.endDate).format(DEFAULT_DATE_FORMAT),
    };

    const response = await memberStore.createProgramAdmission({
      programId: form.data.id,
      admissionInput,
    });

    if (response.isSuccess) {
      setShowModal(false);
      form.reset();
    } else {
      showAlert(
        'Could not enroll member in selected program.',
        ALERT_TYPES.ERROR,
      );
    }
  };

  return (
    <SaveCancelModal
      open={open}
      setShowModal={setShowModal}
      title={`Enroll <strong>${member.name}</strong> in a program`}
      onSave={onSave}
      disableSave={Boolean(selectItems.length <= 0 || !form.canSave())}>
      <ItemSelection
        admission={form.data}
        setAdmission={form.setData}
        selectOptions={selectItems}
        label="Programs"
        programAdmission={programAdmission}
      />
      <ErrorMessage
        name="endDate"
        errors={form.errors}
        touched={{endDate: form.data.endDate?.length > 0}}
      />
    </SaveCancelModal>
  );
};
